import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Star, MapPin, Award, Users, CheckCircle, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../services/authService';

const VendorSpotlight = ({ 
  vendors = null,
  title = "Vendor Spotlight",
  subtitle = "Featured Professional of the Month",
  autoRotate = true,
  rotateInterval = 8000 // 8 seconds
}) => {
  // Fetch vendor spotlights from API
  const { data: spotlightsResponse, isLoading } = useQuery({
    queryKey: ['vendorSpotlights'],
    queryFn: async () => {
      const response = await api.get('/vendor-spotlights', {
        params: { limit: 10 }
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1, // Only retry once on failure
  });

  // Transform API data to match component format
  const transformSpotlightData = (spotlight) => ({
    id: spotlight.id,
    name: spotlight.name,
    category: spotlight.category,
    description: spotlight.description,
    rating: spotlight.rating || 0,
    reviewCount: spotlight.review_count || 0,
    completedEvents: spotlight.completed_events || 0,
    location: spotlight.location,
    image: spotlight.image || 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=800&h=600&fit=crop',
    profileImage: spotlight.profile_image || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop',
    features: spotlight.features || [],
    badge: spotlight.badge || '',
    yearsInBusiness: spotlight.years_in_business || 0,
    startingPrice: spotlight.starting_price || 0,
    vendorId: spotlight.vendor_id
  });

  // Get spotlights from API
  const apiSpotlights = spotlightsResponse?.data 
    ? spotlightsResponse.data.map(transformSpotlightData)
    : [];

  // Determine which vendors to use: provided vendors or API data
  const spotlightVendors = vendors || apiSpotlights;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset index when vendors change
  useEffect(() => {
    if (spotlightVendors.length > 0 && currentIndex >= spotlightVendors.length) {
      setCurrentIndex(0);
    }
  }, [spotlightVendors.length, currentIndex]);

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % spotlightVendors.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + spotlightVendors.length) % spotlightVendors.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Auto-rotate functionality
  useEffect(() => {
    if (!autoRotate || spotlightVendors.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % spotlightVendors.length);
    }, rotateInterval);

    return () => clearInterval(interval);
  }, [autoRotate, rotateInterval, spotlightVendors.length]);

  const spotlightVendor = spotlightVendors[currentIndex];

  // Skeleton Loading Component
  const SkeletonLoader = () => (
    <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      <div className="page-container relative z-10">
        {/* Header Skeleton */}
        <div className="text-center mb-12 animate-pulse">
          <div className="inline-block h-8 w-48 bg-gray-200 rounded-full mb-4"></div>
          <div className="h-10 w-64 bg-gray-200 rounded mx-auto mb-3"></div>
          <div className="h-5 w-96 bg-gray-200 rounded mx-auto"></div>
        </div>

        {/* Main Content Card Skeleton */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 relative">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Image Section Skeleton */}
            <div className="relative h-[400px] md:h-full min-h-[500px] bg-gray-200">
              <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]"></div>
              {/* Badge Skeleton */}
              <div className="absolute top-6 left-6">
                <div className="h-8 w-32 bg-gray-300 rounded-full"></div>
              </div>
              {/* Stats Skeleton */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-32 bg-gray-300/50 rounded-lg"></div>
                  <div className="h-10 w-28 bg-gray-300/50 rounded-lg"></div>
                </div>
              </div>
            </div>

            {/* Content Section Skeleton */}
            <div className="p-8 md:p-12 space-y-6">
              {/* Header Skeleton */}
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="h-8 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-5 w-1/2 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
              </div>

              {/* Description Skeleton */}
              <div className="space-y-2">
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse"></div>
              </div>

              {/* Features Skeleton */}
              <div className="space-y-2">
                <div className="h-5 w-48 bg-gray-200 rounded animate-pulse"></div>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                ))}
              </div>

              {/* Stats Skeleton */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="space-y-2">
                  <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>

              {/* CTA Buttons Skeleton */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <div className="flex-1 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
                <div className="flex-1 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Dot Indicators Skeleton */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-2 h-2 bg-gray-300 rounded-full"></div>
          ))}
        </div>
      </div>
    </section>
  );

  // Show skeleton while loading
  if (isLoading && !vendors) {
    return <SkeletonLoader />;
  }

  // Don't render if no vendors available
  if (!spotlightVendor || spotlightVendors.length === 0) {
    return null;
  }

  return (
    <section className="py-8 sm:py-12 md:py-16 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-100 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="page-container relative z-10 px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-black text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            <Award className="w-3 h-3 sm:w-4 sm:h-4" />
            {subtitle}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 px-2">
            {title}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-2">
            Meet the exceptional professionals who bring your events to life
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-gray-100 relative">
          {/* Navigation Arrows */}
          {spotlightVendors.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                disabled={isAnimating}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 backdrop-blur-sm hover:bg-white p-2 sm:p-3 rounded-full shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
                aria-label="Previous vendor"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 group-hover:text-black" />
              </button>
              
              <button
                onClick={handleNext}
                disabled={isAnimating}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 backdrop-blur-sm hover:bg-white p-2 sm:p-3 rounded-full shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
                aria-label="Next vendor"
              >
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 group-hover:text-black" />
              </button>
            </>
          )}

          <div className="grid md:grid-cols-2 gap-0">
            {/* Image Section */}
            <div className="relative h-[300px] sm:h-[400px] md:h-full md:min-h-[500px] order-2 md:order-1">
              <img
                key={currentIndex}
                src={spotlightVendor.image}
                alt={spotlightVendor.name}
                className="w-full h-full object-cover transition-opacity duration-500"
                style={{ opacity: isAnimating ? 0.5 : 1 }}
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              
              {/* Badge */}
              <div className="absolute top-3 sm:top-4 md:top-6 left-3 sm:left-4 md:left-6">
                <div className="bg-white text-black px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full text-xs sm:text-sm font-light shadow-lg flex items-center gap-1 sm:gap-2">
                  <Award className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500" />
                  <span className="truncate max-w-[120px] sm:max-w-none">{spotlightVendor.badge}</span>
                </div>
              </div>

              {/* Stats overlay */}
              <div className="absolute bottom-3 sm:bottom-4 md:bottom-6 left-3 sm:left-4 md:left-6 right-3 sm:right-4 md:right-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 md:gap-4 text-white">
                  <div className="bg-white/20 backdrop-blur-md rounded-lg px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 flex items-center gap-1 sm:gap-2">
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                    <span className="font-bold text-sm sm:text-base">{spotlightVendor.rating}</span>
                    <span className="text-xs sm:text-sm">({spotlightVendor.reviewCount} reviews)</span>
                  </div>
                  <div className="bg-white/20 backdrop-blur-md rounded-lg px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 flex items-center gap-1 sm:gap-2">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span className="font-bold text-sm sm:text-base">{spotlightVendor.completedEvents}+</span>
                    <span className="text-xs sm:text-sm">Events</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-4 sm:p-6 md:p-8 lg:p-12 flex flex-col justify-between transition-opacity duration-500 order-1 md:order-2" style={{ opacity: isAnimating ? 0.5 : 1 }}>
              {/* Vendor Info */}
              <div key={`content-${currentIndex}`}>
                <div className="flex items-start justify-between mb-3 sm:mb-4 gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 break-words">
                      {spotlightVendor.name}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 font-medium mb-2 sm:mb-3">
                      {spotlightVendor.category}
                    </p>
                    <div className="flex items-center text-gray-500 text-xs sm:text-sm gap-1 sm:gap-2">
                      <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{spotlightVendor.location}</span>
                    </div>
                  </div>
                  
                  {/* Profile Image */}
                  <img
                    src={spotlightVendor.profileImage}
                    alt={`${spotlightVendor.name} profile`}
                    className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full object-cover border-2 sm:border-4 border-white shadow-lg flex-shrink-0"
                  />
                </div>

                {/* Description */}
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                  {spotlightVendor.description}
                </p>

                    {/* Features */}
                    <div className="mb-4 sm:mb-6">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">What Makes Them Special:</h4>
                      <ul className="space-y-1.5 sm:space-y-2">
                        {spotlightVendor.features.slice(0, 6).map((feature, index) => (
                          <li key={index} className="flex items-start text-gray-700">
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-black mr-2 sm:mr-3 flex-shrink-0 mt-0.5 font-light" />
                            <span className="text-xs sm:text-sm break-words">{feature}</span>
                          </li>
                        ))}
                        {spotlightVendor.features.length > 6 && (
                          <li className="text-xs sm:text-sm text-gray-500 italic">
                            +{spotlightVendor.features.length - 6} more
                          </li>
                        )}
                      </ul>
                    </div>

                {/* Additional Stats */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl">
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-gray-900">
                      {spotlightVendor.yearsInBusiness}+
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">Years Experience</div>
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-gray-900">
                      ${spotlightVendor.startingPrice}+
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">Starting Price</div>
                  </div>
                </div>
              </div>

                  {/* CTA Button */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2 sm:pt-0">
                    {spotlightVendor.vendorId ? (
                      <Link
                        to={`/vendor/${spotlightVendor.vendorId}`}
                        className="flex-1 bg-black text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl font-light hover:bg-gray-800 transition-all text-center flex items-center justify-center gap-2 group text-sm sm:text-base"
                      >
                        View Full Profile
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ) : (
                      <Link
                        to={`/services/${spotlightVendor.id}`}
                        className="flex-1 bg-black text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl font-light hover:bg-gray-800 transition-all text-center flex items-center justify-center gap-2 group text-sm sm:text-base"
                      >
                        View Full Profile
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    )}
                  </div>
            </div>
          </div>
        </div>

        {/* Dot Indicators */}
        {spotlightVendors.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 sm:mt-8">
            {spotlightVendors.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isAnimating) {
                    setIsAnimating(true);
                    setCurrentIndex(index);
                    setTimeout(() => setIsAnimating(false), 500);
                  }
                }}
                className={`transition-all ${
                  index === currentIndex
                    ? 'w-6 sm:w-8 h-1.5 sm:h-2 bg-black rounded-full'
                    : 'w-1.5 sm:w-2 h-1.5 sm:h-2 bg-gray-300 rounded-full hover:bg-gray-400'
                }`}
                aria-label={`Go to vendor ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Bottom Note */}
        <div className="text-center mt-4 sm:mt-6 px-2">
          <p className="text-gray-500 text-xs sm:text-sm">
            ✨ Selected based on exceptional service quality, customer satisfaction, and professional excellence
          </p>
        </div>
      </div>
    </section>
  );
};

export default VendorSpotlight;

